<script setup>
// §8.5 Checkbox — a NATIVE <input type="checkbox">, styled with CSS. Supports
// the indeterminate state (used by DataTable's select-all). The only JS is
// setting the DOM `indeterminate` property, which has no HTML attribute — the
// tick/dash glyphs and every other state are pure CSS.
import { ref, computed, watch, onMounted } from 'vue'
import LIcon from '../base/Icon.vue'

const props = defineProps({
	label: { type: String, default: null },
	// true | false | 'indeterminate'
	modelValue: { type: [Boolean, String], default: false },
	disabled: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue'])

const el = ref(null)
const checked = computed(() => props.modelValue === true)
const indeterminate = computed(() => props.modelValue === 'indeterminate')

function applyIndeterminate()
{
	if (el.value) el.value.indeterminate = indeterminate.value
}
watch(indeterminate, applyIndeterminate)
onMounted(applyIndeterminate)

function onChange(e)
{
	emit('update:modelValue', e.target.checked)
}
</script>

<template>
	<label class="l-checkbox" :class="{ 'is-disabled': disabled }">
		<span class="box">
			<input ref="el" type="checkbox" :checked="checked" :disabled="disabled"
				:aria-checked="indeterminate ? 'mixed' : checked"
				@change="onChange" />
			<LIcon name="check" :size="12" class="l-mark check" />
			<LIcon name="minus" :size="12" class="l-mark minus" />
		</span>
		<span v-if="label" class="lbl">{{ label }}</span>
	</label>
</template>

<style scoped>
.l-checkbox
{
	display: inline-flex;
	align-items: center;
	gap: var(--sp-2);
	cursor: pointer;
	min-height: var(--target-min);
}

.box
{
	position: relative;
	display: inline-flex;
	width: 18px;
	height: 18px;
	flex: none;
}

input
{
	appearance: none;
	-webkit-appearance: none;
	width: 18px;
	height: 18px;
	margin: 0;
	border-radius: 5px;
	border: 1.5px solid var(--decorative);
	background: var(--surface);
	cursor: pointer;
	transition: background var(--t-fast) var(--ease), border-color var(--t-fast) var(--ease);
}

input:checked,
input:indeterminate
{
	background: var(--interactive);
	border-color: var(--interactive);
}

/* Matches LSelect's disabled treatment: sunken ground, muted text, no-drop cursor. */
.l-checkbox.is-disabled
{
	cursor: not-allowed;
}

.l-checkbox.is-disabled .lbl
{
	color: var(--text-3);
}

input:disabled
{
	background: var(--surface-sunken);
	border-color: var(--decorative);
	cursor: not-allowed;
}

input:disabled:checked,
input:disabled:indeterminate
{
	background: var(--text-3);
	border-color: var(--text-3);
}

/* `l-mark`, not `mark` — the legacy global Bootstrap in styles-version-1.scss
   still ships `mark, .mark { background-color: #fff3cd }`, which painted a pale
   yellow square over the box. Scoped styles keep our CSS in; they don't keep
   the old global CSS out, so the class name itself has to not collide. */
.l-mark
{
	position: absolute;
	inset: 0;
	color: var(--on-emphasis);
	background: transparent;
	opacity: 0;
	pointer-events: none;
}

input:checked~.check
{
	opacity: 1;
}

input:indeterminate~.minus
{
	opacity: 1;
}

.lbl
{
	font-size: var(--text-base);
	color: var(--text);
}
</style>
